// Code taken from
// https://github.com/radix-ui/primitives/blob/c92d43343f1233a94a41d12c5eb56a42b72dc98e/packages/react/compose-refs/src/composeRefs.tsx

import { useCallback } from "react";

type PossibleRef<T> = React.Ref<T> | undefined;

/**
 * Set a given ref to a given value
 * This utility takes care of different types of refs: callback refs and RefObject(s)
 */
export function setRef<T>(ref: PossibleRef<T>, value: T) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref !== null && ref !== undefined) {
    (ref as React.MutableRefObject<T>).current = value;
  }
}

/**
 * A utility to compose multiple refs together
 * Accepts callback refs and RefObject(s)
 */
export function composeRefs<T>(...refs: PossibleRef<T>[]) {
  return (node: T | null) => refs.forEach((ref) => setRef(ref, node));
}

/**
 * A custom hook that composes multiple refs.
 * Accepts callback refs and RefObject(s)
 *
 * Example usage:
 * ```tsx
 * const DialogTrigger = forwardRef((props, forwardedRef) => {
 *   const context = useDialogContext();
 *   const composedTriggerRef = useComposedRefs(forwardedRef, context.triggerRef);
 *   return <button type="button" ref={composedTriggerRef} />;
 * });
 * ```
 */
export function useComposedRefs<T>(...refs: PossibleRef<T>[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(composeRefs(...refs), refs);
}
